import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { startPolling } from '@/utils/layoutPolling';
import { playNotificationSound, isNotificationSoundEnabled, setNotificationSoundEnabled } from '@/utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MarketingNotificationsBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled());
  const prevUnreadRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Okt 2026 (Fase 6): startPolling menggantikan setInterval. Tidak menumpuk
    // saat sinyal lemah, berhenti saat tab tersembunyi/offline, jeda melebar saat gagal.
    const stop = startPolling(() => fetchNotifications(), { intervalMs: 30000 });
    return () => stop();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let notifs = [];
      let unread = 0;
      
      if (response.data && response.data.notifications) {
        notifs = response.data.notifications;
        unread = response.data.unread_count || 0;
      } else if (Array.isArray(response.data)) {
        notifs = response.data;
        unread = notifs.filter(n => !n.is_read).length;
      }
      
      // Sort: unread first, then by created_at descending
      notifs.sort((a, b) => {
        if (a.is_read !== b.is_read) {
          return a.is_read ? 1 : -1;
        }
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      
      setNotifications(notifs);
      setUnreadCount(unread);

      // Play sound if unread increased (skip first load)
      if (prevUnreadRef.current !== null && unread > prevUnreadRef.current) {
        playNotificationSound();
      }
      prevUnreadRef.current = unread;
      return true;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return false;
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/${notificationId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    
    // Navigate based on notification type or action_url
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.order_id) {
      navigate(`/marketing/orders/${notification.order_id}`);
    } else if (notification.type === 'ANNOUNCEMENT') {
      navigate('/marketing/knowledge-center');
    } else {
      navigate('/marketing/notifications');
    }
  };

  const handleViewAll = () => {
    navigate('/marketing/notifications');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'WORKFLOW_UPDATE':
      case 'NKS_OTW':
      case 'NKS_ARRIVED':
      case 'NKS_START_SERVICE':
      case 'NKS_SERVICE_COMPLETED':
        return '🚗';
      case 'REMINDER_H1':
      case 'MKT_REMINDER_H1':
        return '⏰';
      case 'NEW_MESSAGE':
      case 'CHAT_OFFLINE':
        return '💬';
      case 'ANNOUNCEMENT':
        return '📢';
      case 'ORDER_CREATED':
        return '📋';
      default:
        return '🔔';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="marketing-notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="px-3 py-2 border-b bg-gradient-to-r from-blue-50 to-white flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm">Notifikasi</h3>
            {unreadCount > 0 && (
              <p className="text-xs text-gray-500">{unreadCount} belum dibaca</p>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const next = !soundEnabled;
              setSoundEnabled(next);
              setNotificationSoundEnabled(next);
              if (next) playNotificationSound();
            }}
            className="p-1.5 rounded hover:bg-blue-100 text-gray-600"
            title={soundEnabled ? 'Bunyikan: aktif' : 'Bunyikan: nonaktif'}
            data-testid="marketing-notif-sound-toggle"
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>

        {/* View All Button */}
        <div className="px-3 py-2 border-b bg-gray-50">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewAll();
            }}
            className="w-full py-2 text-center text-sm text-blue-600 hover:text-blue-700 font-semibold hover:bg-blue-50 rounded transition-colors"
          >
            📋 Lihat Semua Notifikasi
          </button>
        </div>

        {notifications.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <div className="text-gray-400 mb-2">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
            </div>
            <p className="text-sm text-gray-500">Tidak ada notifikasi</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.slice(0, 10).map((notification) => (
              <DropdownMenuItem
                key={notification.notification_id}
                className={`p-3 cursor-pointer border-b ${!notification.is_read ? 'bg-blue-50' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="w-full">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                      <h4 className="font-medium text-sm">{notification.title}</h4>
                    </div>
                    {!notification.is_read && (
                      <span className="flex h-2 w-2 rounded-full bg-blue-600 mt-1 flex-shrink-0"></span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2 ml-7">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 ml-7">
                    {new Date(notification.created_at).toLocaleString('id-ID')}
                  </p>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
